/**
 * Route suggestions for 404 page: collect all app paths and find best string-similar match.
 */

import { pluginRegistry } from '../plugins/PluginRegistry';

/** Core app paths (from App.tsx switch + public routes). Must stay in sync with App routing. */
const CORE_PATHS = [
  '/',
  '/chat',
  '/search',
  '/data',
  '/knowledge',
  '/entities',
  '/reports',
  '/sources',
  '/jobs',
  '/settings',
  '/profile',
  '/teams',
  '/audit',
  '/evidence',
  '/policy',
  '/notifications',
  '/notifications/preferences',
];

const PUBLIC_ROUTES = [
  '/login',
  '/reset-password',
  '/accept-invitation',
  '/auth/callback',
  '/auth/azure/callback',
];

function collectPathsFromNavItems(items: { path: string; children?: { path: string }[] }[]): string[] {
  const out: string[] = [];
  for (const item of items) {
    out.push(item.path);
    if (item.children?.length) {
      for (const c of item.children) out.push(c.path);
    }
  }
  return out;
}

/** Returns all known navigable paths (core + public + plugin routes + plugin sidebar). */
export function getAllPaths(): string[] {
  const pluginRoutes = pluginRegistry.getAllRoutes().map((r) => r.path);
  const pluginNav = pluginRegistry.getAllSidebarItems();
  const pluginPaths = collectPathsFromNavItems(pluginNav);
  const set = new Set<string>([
    ...CORE_PATHS,
    ...PUBLIC_ROUTES,
    ...pluginRoutes,
    ...pluginPaths,
  ]);
  return Array.from(set);
}

/**
 * Levenshtein distance between two strings.
 */
function levenshtein(a: string, b: string): number {
  const an = a.length;
  const bn = b.length;
  const dp: number[][] = Array(an + 1)
    .fill(null)
    .map(() => Array(bn + 1).fill(0));
  for (let i = 0; i <= an; i++) dp[i][0] = i;
  for (let j = 0; j <= bn; j++) dp[0][j] = j;
  for (let i = 1; i <= an; i++) {
    for (let j = 1; j <= bn; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[an][bn];
}

/**
 * Similarity in [0, 1]; 1 = identical. Uses length-normalized Levenshtein.
 * Works for both extra chars (proposals-managementt) and missing chars (external-dat).
 */
function similarity(a: string, b: string): number {
  if (a === b) return 1;
  if (!a.length || !b.length) return 0;
  const maxLen = Math.max(a.length, b.length);
  const dist = levenshtein(a, b);
  return 1 - dist / maxLen;
}

/**
 * Boost score when one path is a prefix of the other (handles missing or extra chars at the end).
 * E.g. /rfp/external-dat is prefix of /rfp/external-data; /rfp/proposals-managementt has prefix /rfp/proposals-management.
 */
function prefixBonus(invalid: string, candidate: string): number {
  if (invalid === candidate) return 1;
  if (candidate.startsWith(invalid) && candidate.length - invalid.length <= 3) return 0.95;
  if (invalid.startsWith(candidate) && invalid.length - candidate.length <= 3) return 0.95;
  return 0;
}

/** Minimum similarity (0–1) to show "Did you mean?" */
const DEFAULT_THRESHOLD = 0.5;

/**
 * Find the best matching path for a given (invalid) path, or null if none above threshold.
 * Uses Levenshtein similarity plus prefix bonus so both typos (extra/missing chars) are suggested.
 */
export function getSuggestedPath(
  invalidPath: string,
  options?: { threshold?: number; paths?: string[] }
): string | null {
  const threshold = options?.threshold ?? DEFAULT_THRESHOLD;
  const candidates = options?.paths ?? getAllPaths();
  const normalizedInvalid = invalidPath.replace(/\/$/, '') || '/';

  let bestPath: string | null = null;
  let bestScore = 0;

  for (const path of candidates) {
    const normalizedCandidate = path.replace(/\/$/, '') || '/';
    const editScore = similarity(normalizedInvalid, normalizedCandidate);
    const prefixScore = prefixBonus(normalizedInvalid, normalizedCandidate);
    const score = Math.max(editScore, prefixScore);
    if (score > bestScore && score >= threshold) {
      bestScore = score;
      bestPath = path;
    }
  }

  return bestPath;
}
